/*
 * Project: bellcloud
 * File: components/common/InstallGuideModal.tsx
 * Description: iOS "홈 화면에 추가" 절차를 스크린샷 캐러셀로 안내하는 모달
 */

import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { ModalShell } from '@/components/ui/common/Modal';

const AUTO_PLAY_INTERVAL_MS = 2500;

const STEP_IMAGES_KO = [
  '/images/pwa/ios_ko_0.webp',
  '/images/pwa/ios_ko_1.webp',
  '/images/pwa/ios_ko_2.webp',
  '/images/pwa/ios_ko_3.webp',
  '/images/pwa/ios_ko_4.webp',
];

const STEP_IMAGES_EN = [
  '/images/pwa/ios_en_0.webp',
  '/images/pwa/ios_en_1.webp',
  '/images/pwa/ios_en_2.webp',
  '/images/pwa/ios_en_3.webp',
  '/images/pwa/ios_en_4.webp',
];

const STEP_LABEL_KEYS = [
  i18nKeys.common.install_app_guide_step0,
  i18nKeys.common.install_app_guide_step1,
  i18nKeys.common.install_app_guide_step2,
  i18nKeys.common.install_app_guide_step3,
  i18nKeys.common.install_app_guide_step4,
] as const;

const STEP_COUNT = STEP_LABEL_KEYS.length;

const CarouselViewport = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  max-height: 55vh;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.group.default};
  touch-action: pan-y;
`;

const CarouselSlide = styled.img<{ offset: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform: translateX(${({ offset }) => offset * 100}%);
  transition: transform 0.35s ease;
  user-select: none;
  -webkit-user-drag: none;
`;

const StepLabel = styled.p`
  margin: ${({ theme }) => theme.spacing[5]}px 0 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  min-height: ${({ theme }) => theme.fontSize.md * 1.3}px;
`;

const DotRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]}px;
  margin-top: ${({ theme }) => theme.spacing[5]}px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: ${({ active }) => (active ? 20 : 8)}px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: ${({ theme, active }) =>
    active ? theme.colors.button.bg.primary.default : theme.colors.border.group.default};
  cursor: pointer;
  transition:
    width 0.2s ease,
    background 0.2s ease;
`;

interface InstallGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * iOS "홈 화면에 추가" 5단계 스크린샷을 캐러셀로 보여준다.
 * 열리면 자동 재생되며, 스와이프/점 클릭으로 수동 이동 시 자동 재생 타이머를 리셋한다.
 */
export const InstallGuideModal: React.FC<InstallGuideModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const touchStartXRef = useRef<number | null>(null);

  const images = i18n.language?.split('-')[0] === 'ko' ? STEP_IMAGES_KO : STEP_IMAGES_EN;

  // 모달이 열릴 때마다 처음 단계로 초기화
  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  // 자동 재생: 마지막 단계에서는 멈춘다
  useEffect(() => {
    if (!isOpen || step >= STEP_COUNT - 1) return;
    const timer = setTimeout(() => setStep(prev => prev + 1), AUTO_PLAY_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isOpen, step]);

  const goTo = (nextStep: number) => {
    setStep(Math.max(0, Math.min(STEP_COUNT - 1, nextStep)));
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX === null) return;

    const deltaX = event.changedTouches[0].clientX - startX;
    const SWIPE_THRESHOLD_PX = 40;
    if (deltaX <= -SWIPE_THRESHOLD_PX) goTo(step + 1);
    else if (deltaX >= SWIPE_THRESHOLD_PX) goTo(step - 1);
  };

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.common.install_app_guide_title)}
      onClose={onClose}
      size="sm"
    >
      <CarouselViewport onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {images.map((src, index) => (
          <CarouselSlide key={src} src={src} offset={index - step} alt="" />
        ))}
      </CarouselViewport>

      <StepLabel>{t(STEP_LABEL_KEYS[step])}</StepLabel>

      <DotRow>
        {STEP_LABEL_KEYS.map((_, index) => (
          <Dot
            key={index}
            active={index === step}
            onClick={() => goTo(index)}
            aria-label={`${index + 1}/${STEP_COUNT}`}
          />
        ))}
      </DotRow>
    </ModalShell>
  );
};
